import React, { createRef } from 'react';
import { afterChange, fetchProducts, setDefaultImage } from '../utils';
import { connect } from 'react-redux';
import loadable from '@loadable/component';
import OrigoArrow from './OrigoArrow';

import '../css/ProductGallery.less';
import Spinner from './Spinner';
import { Card } from 'antd';

const Slider = loadable(() => import(/* webpackPreload: true */ './CustomSlick'));
const ProductCard = loadable(
    () =>
        import(
            /* webpackChunkName: "ProductCard" */
            /* webpackPreload: true */
            './ProductCard'
        )
);

class ProductGallery extends React.Component {
    constructor(props) {
        super(props);
        this.fetchProducts = fetchProducts.bind(this);
        this.afterChange = afterChange.bind(this);
        this.productsElement = createRef();
        this.slider = undefined;
        this.state = {
            products: this.props.products ? this.props.products : [],
            loading: !this.props.products,
            slidesToShow: 4,
            marginPerItem: 15,
            cardHeight: null,
            productIdsOnWishList: [],
            productsWidth: null,
            heading: props.heading,
            index: 0,
            cardStyle: null,
            resize: props.resize,
            showAllUrl: false,
            showCount: props.showCount,
            slim: false,
            showArrows: !this.props.slim,
            browserWidth: null,
            totalNumProducts: this.props.totalNumProducts,
        };
    }

    componentDidMount() {
        if (!this.props.products) {
            this.fetchProducts(this.props.url, this.props.body, this.props.customerId);
        } else {
            window.requestAnimationFrame(() => this.scaleComponent());
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.resize !== this.props.resize) {
            this.setState({ resize: this.props.resize });
        }

        if (this.state.slim !== this.props.slim) {
            this.setState({ slim: this.props.slim });
        }
        if (this.props.productIdsOnWishList !== this.state.productIdsOnWishList) {
            this.setState({ productIdsOnWishList: this.props.productIdsOnWishList });
        }
        window.requestAnimationFrame(() => this.scaleComponent());
    }

    scaleComponent = () => {
        if (
            this.productsElement.current &&
            this.state.productsWidth !== this.productsElement.current.getBoundingClientRect().width
        ) {
            let dimensions = this.productsElement.current.getBoundingClientRect();
            let slidesToShow = 4;
            let listWidth = dimensions.width;
            let cardStyle = 'desktop';
            let showArrows = this.state.showArrows;
            if (showArrows) listWidth += 100;
            if (listWidth > 1550) {
                if (this.state.products.length > 4) slidesToShow = 5;
                else slidesToShow = 4;
            } else if (listWidth > 1200) {
                slidesToShow = 4;
                cardStyle = 'tablet';
            } else if (listWidth > 950) {
                slidesToShow = 3;
                cardStyle = 'tablet';
            } else if (listWidth > 600) {
                slidesToShow = 2;
            } else {
                slidesToShow = 1;
            }
            if (listWidth < 700) {
                cardStyle = 'mobile';
            }
            let browserWidth = this.state.browserWidth;
            if (!browserWidth || browserWidth !== this.props.browserWidth) {
                if (!this.props.slim) {
                    if (listWidth <= 600) showArrows = false;
                    else if (listWidth > 600) showArrows = true;
                } else {
                    showArrows = false;
                }
                this.setState({
                    productsWidth: listWidth,
                    slidesToShow: slidesToShow,
                    cardStyle: cardStyle,
                    showArrows: showArrows,
                    browserWidth: this.props.browserWidth,
                });
            }
        }
    };

    createProductCards = () => {
        let cards = [];
        let productsOnWishList = this.state.productIdsOnWishList;
        for (let i = 0; i < this.state.products.length; i++) {
            let prod = this.state.products[i];
            let isOnWishList = false;
            if (productsOnWishList.includes(prod.id)) {
                isOnWishList = true;
            }

            let quantityInCart = 0;
            let basketId = 0;

            for (let i = 0; i < this.props.cart.products.length; i++) {
                let basket = this.props.cart.products[i];
                if (basket.prodId === prod.id) {
                    quantityInCart = basket.quantity;
                    basketId = basket.basketId;
                }
            }

            cards.push(
                <ProductCard
                    image={prod.image}
                    title={prod.name}
                    price={window.displayPriceWithTax ? prod.priceIncTax : prod.priceExTax}
                    discountPrice={
                        window.displayPriceWithTax
                            ? prod.specialPriceIncTax
                            : prod.specialPriceExTax
                    }
                    id={prod.id}
                    bannerText={prod.custom7}
                    key={i}
                    height={this.state.cardHeight}
                    cardStyle={this.state.cardStyle}
                    isOnWishList={isOnWishList}
                    hideCompare={true}
                    quantity={
                        prod.stock10Quantity && process.env.REACT_APP_STORE === 'store1'
                            ? prod.stock10Quantity
                            : prod.quantity
                    }
                    reykjavik={prod.reykjavikQuantity}
                    akureyri={prod.akureyriQuantity}
                    expected={prod.expected}
                    specialOrder={prod.custom4}
                    contentType={prod.contentType}
                    quantityInCart={quantityInCart}
                    basketId={basketId}
                    sku={prod.sku}
                    bundledProduct={this.props.bundle}
                    quantityInBundle={prod.quantityInBundle}
                    insurancesAvailable={prod.insurancesAvailable}
                    store={this.props.store}
                    baseUrl={this.props.baseUrl}
                />
            );
        }
        return cards;
    };

    createAlternativeStoreCards = () => {
        const baseUrl = this.props.baseUrl;
        return this.props.products.map((product) => {
            return (
                <a href={baseUrl + 'SelectProd?prodId=' + product.id} target="__blank">
                    <Card
                        hoverable
                        cover={
                            <img
                                alt={product.name}
                                src={'/images' + product.image}
                                onError={(e) => setDefaultImage(e)}
                            />
                        }
                        className="alt-store-card"
                    >
                        {product.name}
                    </Card>
                </a>
            );
        });
    };

    render() {
        let centerMode = this.state.slidesToShow === 1;
        let showArrows = this.state.showArrows;
        let settings = {
            dots: false,
            accessibility: true,
            infinite: false,
            speed: 500,
            cssEase: 'ease-in-out',
            slidesToShow: this.state.slidesToShow,
            slidesToScroll: this.state.slidesToShow,
            swipe: true,
            swipeToSlide: true,
            touchThreshold: 5,
            arrows: showArrows,
            nextArrow: (
                <OrigoArrow
                    direction={'next'}
                    disabled={
                        this.state.index + this.state.slidesToShow >= this.state.products.length
                    }
                />
            ),
            prevArrow: <OrigoArrow direction={'prev'} disabled={this.state.index === 0} />,
            centerMode: centerMode,
            centerPadding: '50px',
            afterChange: this.afterChange,
            lazyLoad: 'progressive',
        };
        if (this.state.products && this.state.products.length > 0) {
            let title = this.state.title ? this.state.title : this.props.heading;
            let url = this.state.searchKey ? '/' + this.state.searchKey : this.props.showAllUrl;
            return (
                <div
                    id={this.props.id}
                    className={
                        'product-gallery' +
                        (this.state.slim ? ' slim' : '') +
                        (showArrows ? ' arrows' : '') +
                        (centerMode ? ' centerMode' : '')
                    }
                    ref={this.productsElement}
                >
                    {title && (
                        <div className={'gallery-header'}>
                            <h1 className={'heading'}>
                                {url ? (
                                    <a
                                        className={'black-link title'}
                                        href={url}
                                        target={this.props.alternativeStore ? '__blank' : ''}
                                    >
                                        {title}
                                    </a>
                                ) : (
                                    title
                                )}
                            </h1>
                            {url ? (
                                <a
                                    className={'show-all-url black-link'}
                                    href={url}
                                    target={this.props.alternativeStore ? '__blank' : ''}
                                >
                                    Skoða allt
                                </a>
                            ) : !this.state.showArrows && this.state.slim ? (
                                <>
                                    <OrigoArrow
                                        onClick={() => this.slider.slickPrev()}
                                        className="small"
                                        direction={'prev'}
                                        disabled={this.state.index === 0}
                                    />
                                    <OrigoArrow
                                        onClick={() => this.slider.slickNext()}
                                        className="small"
                                        direction={'next'}
                                        disabled={
                                            this.state.index + this.state.slidesToShow >=
                                            this.state.products.length
                                        }
                                    />
                                </>
                            ) : (
                                ''
                            )}
                        </div>
                    )}
                    <Slider
                        ref={(slider) => (this.slider = slider)}
                        {...settings}
                        className={
                            (this.state.slidesToShow === 1 ? 'mobile-slider' : '') +
                            (this.props.centered &&
                            this.state.slidesToShow === 5 &&
                            this.state.products.length < 5
                                ? ' centered-slider'
                                : '')
                        }
                    >
                        {this.props.alternativeStore
                            ? this.createAlternativeStoreCards()
                            : this.createProductCards()}
                    </Slider>
                    {this.state.showCount && this.state.totalNumProducts > 0 ? (
                        <div className={'count'}>
                            Sýni {this.state.products.length} af {this.state.totalNumProducts} vörum
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            );
        } else if (this.state.loading) {
            return (
                <div className={'product-gallery-spinner'}>
                    <Spinner className={'center-spinner'} size={120} />
                </div>
            );
        } else return null;
    }
}

const mapStateToProps = (state) => {
    return {
        productIdsOnWishList: state.wishList.productIds,
        browserWidth: state.browserWidth,
        cart: state.cart,
        customerId: state.customerId,
    };
};

export default connect(mapStateToProps, null)(ProductGallery);
